/* eslint-disable eslint-comments/no-restricted-disable -- var colors */
/* eslint-disable sonarjs/no-duplicate-string -- token literal values (e.g. "var(--color-grey-0)") intentionally repeat across many light/dark theme entries */

import type { Theme } from "./types";

export const DEFAULT_THEME: Theme = {
  light: {
    ["--brand"]: "var(--color-royal-blue-400)",
    ["--brand-button-text"]: "var(--color-grey-0)",
    // Text colors
    ["--color-text-lightest"]: "var(--color-text-tertiary)",
    ["--color-text-light"]: "var(--color-grey-500)",
    ["--color-text-default"]: "var(--color-grey-600)",
    ["--color-text-dark"]: "var(--color-text-primary)",
    ["--color-text-timestamp-light"]: "var(--color-grey-400)",
    ["--color-text-very-dark"]: "var(--color-grey-800)", //gray800 in design
    ["--color-text-darkest"]: "var(--color-grey-900)",
    ["--color-text-badge"]: "var(--color-grey-0)",
    ["--color-text-feature-dark"]: "var(--color-grey-50)",
    ["--color-text-feature-light"]: "var(--color-grey-200)",
    ["--color-text-icon-text"]: "var(--color-grey-0)",
    ["--color-community-active-item-text-color"]: "var(--color-grey-0)",
    ["--color-community-sidebar-text-on-accent"]: "var(--color-grey-0)",
    ["--color-text-selector-active"]: "var(--color-grey-800)",
    ["--color-text-date-blue"]: "var(--color-royal-blue-400)",
    ["--color-text-chip"]: "var(--color-text-success)",
    ["--color-text-placeholder"]: "var(--color-grey-600)", // original #545859 appears to be a typo for grey-600 #545861
    ["--text-brand-bg"]: "var(--color-grey-0)",
    ["--color-messaging-info-blue"]: "66 98 240", // NO COMPASS TOKEN — raw RGB channel format (#4262F0), incompatible with CSS var values -- Adriana told color-border-info
    ["--color-circle-button-text"]: "var(--color-grey-0)",
    ["--color-stroke-foreground"]: "var(--color-grey-800)",
    ["--color-stroke-background"]: "var(--color-grey-200)", // original #E4E8EB appears to be a typo for grey-200 #E4E7EB
    ["--color-text-workflows-icon"]: "var(--color-text-info)",
    ["--color-text-green-success"]: "var(--color-green-600)",
    ["--color-text-green-success-dark"]: "var(--color-text-success)",
    ["--color-text-blue-highlight"]: "var(--color-blue-600)",
    // Background colors
    ["--color-background-tertiary"]: "var(--color-grey-100)",
    ["--color-background-secondary-dark"]: "var(--color-background-secondary)",
    ["--color-background-overlay-dark"]: "var(--color-grey-200)",
    ["--color-background-overlay-light"]: "var(--color-background-overlay)",
    ["--color-background-badge"]: "var(--color-grey-500)",
    ["--color-background-light-blue"]: "var(--color-background-info-light)",
    ["--color-background-selector-active"]: "var(--color-grey-50)",
    ["--color-background-avatar-primary"]: "var(--color-grey-0)",
    ["--color-background-avatar-secondary"]: "var(--color-grey-300)",
    ["--color-background-dark"]: "var(--color-grey-400)",
    ["--color-background-dark-button"]: "var(--color-grey-800)",
    ["--color-background-feature"]: "var(--color-haiti-400)",
    ["--color-online-background"]: "var(--color-background-success)",
    ["--color-background-banner"]: "var(--color-grey-800)",
    ["--color-background-plus-banner"]: "var(--color-grey-900)",
    ["--color-background-toggle-active"]: "var(--color-grey-700)",
    ["--color-background-toggle-inactive"]: "var(--color-grey-400)",
    ["--color-background-modal-overlay"]: "var(--color-background-hover)",
    ["--color-background-text-editor-modifier"]: "#1a1d21", // NO COMPASS TOKEN — close to grey-900 #191b1f but not exact
    ["--color-background-text-editor-modifier-hover"]: "#2C2F33", // NO COMPASS TOKEN — close to grey-800 #2b2e33 but not exact
    ["--color-background-text-editor-modifier-active"]: "#444649", // NO COMPASS TOKEN — close to grey-700 #42464d but not exact
    ["--color-toggle-checked"]: "var(--color-grey-700)",
    ["--color-toggle-unchecked"]: "var(--color-grey-400)",
    ["--color-toggle-circle-checked"]: "var(--color-grey-0)",
    ["--color-toggle-circle-unchecked"]: "var(--color-grey-0)",
    ["--color-toggle-fullscreen"]: "var(--color-grey-900)",
    ["--color-circle-button-background"]: "var(--color-grey-800)",
    ["--color-circle-button-background-hover"]: "#41464E", // NO COMPASS TOKEN — close to grey-700 #42464d but not exact
    ["--color-ai-start"]: "var(--color-royal-blue-400)",
    ["--color-ai-end"]: "#AF81EA", // NO COMPASS TOKEN — not in palette (indigo-300 is #aa91dd, different)
    ["--color-chip-background"]: "var(--color-background-success-light)",
    ["--color-search-yellow-background"]: "var(--color-background-highlight)",
    ["--color-background-workflows-icon-wrapper"]:
      "var(--color-background-info-light)",
    ["--color-background-workflows-badge"]:
      "var(--color-background-info-light)",
    ["--color-background-xy-flow"]: "var(--color-background-secondary)",
    ["--color-background-workflows-v2-error"]: "var(--color-red-50)",
    // Border colors
    ["--color-border-secondary-dark"]: "#F4F4F5", // NO COMPASS TOKEN — not in palette (between grey-50 and grey-100)
    ["--color-border-tertiary"]: "var(--color-grey-0)",
    ["--color-border-dark"]: "var(--color-grey-300)",
    ["--color-border-darkest"]: "var(--color-grey-900)",
    ["--color-border-very-dark"]: "#52525B", // NO COMPASS TOKEN — not in grey palette
    ["--color-border-light"]: "#9CA3AF", // NO COMPASS TOKEN — not in Compass grey palette
    ["--color-border-input"]: "var(--color-grey-300)",
    ["--color-border-selector-inactive"]: "var(--color-grey-300)",
    ["--color-border-circle-button"]: "var(--color-grey-800)",
    ["--color-border-circle-button-hover"]: "#41464E", // NO COMPASS TOKEN — close to grey-700 #42464d but not exact
    ["--color-border-workflows-v2-error"]: "var(--color-red-300)",
    // Community sidebar & switcher colors
    ["--color-community-sidebar-text"]: "var(--color-grey-600)",
    ["--color-community-sidebar-icon"]: "var(--color-grey-200)",
    ["--color-community-sidebar-icon-hover"]: "var(--color-grey-200)",
    ["--color-community-sidebar-badge-text"]: "var(--color-grey-0)",
    ["--color-community-sidebar-background"]: "var(--color-grey-0)",
    ["--color-community-sidebar-active-background"]:
      "var(--color-royal-blue-400)",
    ["--color-community-sidebar-hover-background"]: "var(--color-grey-100)",
    ["--color-community-sidebar-badge-background"]:
      "var(--color-background-danger)",
    ["--color-message-highlight-background"]:
      "var(--color-background-warning-light)",
    ["--color-community-sidebar-border"]: "var(--color-grey-200)",
    ["--color-community-switcher-new-community-button-icon"]: "#ffffff66", // NO COMPASS TOKEN — grey-0 with alpha, no equivalent CSS var
    ["--color-community-switcher-background"]: "var(--color-grey-0)",
    ["--color-community-switcher-new-community-button-bg"]: "#ffffff1a", // NO COMPASS TOKEN — grey-0 with alpha, no equivalent CSS var
    // Event
    ["--color-event-sidebar-item"]: "var(--color-grey-200)",
    //Ring colors
    ["--color-ring-primary"]: "var(--color-grey-0)",
    ["--color-ring-secondary"]: "var(--color-grey-500)",
    // Support widget colors
    ["--color-support-widget-background"]: "var(--color-royal-blue-400)",
    // Emoji picker colors — raw RGB channel format, cannot use CSS vars directly
    ["--color-background-emoji-picker"]: "255,255,255", // NO COMPASS TOKEN (FORMAT) — grey-0 in rgb channels for use with rgb()
    ["--color-background-emoji-picker-hover"]: "113, 118, 128", // NO COMPASS TOKEN (FORMAT) — grey-500 in rgb channels for use with rgb()
    ["--color-text-emoji"]: "25, 40, 58", // NO COMPASS TOKEN — #19283a in rgb channels, not in palette
    // Circle v2.1 colors
    ["--color-sidebar-footer-live-indicator"]:
      "var(--color-background-inverse)", // "#0F0C35", // NO COMPASS TOKEN — not in palette
    ["--color-community-header-background"]: "var(--color-grey-0)",
    ["--color-community-header-hover-background"]: "var(--color-grey-50)",
    ["--color-community-header-text"]: "var(--color-grey-600)",
    ["--color-community-header-active-text"]: "var(--color-grey-600)",
    ["--color-community-header-active-background"]: "var(--color-grey-100)",
    // Feedback colors
    ["--color-feedback-error"]: "var(--color-red-600)",
    ["--color-border-workflows-icon-wrapper"]: "var(--color-border-info)",
  },
  dark: {
    ["--brand"]: "var(--color-royal-blue-400)",
    ["--brand-button-text"]: "var(--color-grey-0)",
    // Text colors
    ["--color-text-darkest"]: "var(--color-grey-50)",
    ["--color-text-very-dark"]: "var(--color-grey-50)",
    ["--color-text-dark"]: "var(--color-grey-100)",
    ["--color-text-timestamp-light"]: "var(--color-grey-400)",
    ["--color-text-default"]: "var(--color-grey-400)",
    ["--color-text-light"]: "var(--color-grey-400)",
    ["--color-text-lightest"]: "var(--color-grey-400)",
    ["--color-text-badge"]: "var(--color-text-primary)",
    ["--color-text-feature-dark"]: "var(--color-grey-50)",
    ["--color-text-feature-light"]: "var(--color-grey-200)",
    ["--color-text-icon-text"]: "var(--color-grey-500)",
    ["--color-community-active-item-text-color"]: "var(--color-grey-0)",
    ["--color-community-sidebar-text-on-accent"]: "var(--color-grey-0)",
    ["--color-text-selector-active"]: "var(--color-grey-100)",
    ["--color-messaging-info-blue"]: "66 98 240", // NO COMPASS TOKEN — raw RGB channel format (#4262F0), incompatible with CSS var values
    ["--color-circle-button-text"]: "var(--color-text-primary)",
    ["--text-brand-bg"]: "var(--color-grey-0)",
    ["--color-stroke-foreground"]: "var(--color-grey-200)",
    ["--color-stroke-background"]: "var(--color-grey-600)",
    ["--color-text-date-blue"]: "var(--color-royal-blue-400)",
    ["--color-text-chip"]: "var(--color-text-success)",
    ["--color-text-workflows-icon"]: "var(--color-text-info)",
    ["--color-text-green-success"]: "var(--color-green-50)",
    ["--color-text-green-success-dark"]: "var(--color-green-50)",
    ["--color-text-blue-highlight"]: "var(--color-grey-0)",
    //Placeholder color
    ["--color-text-placeholder"]: "var(--color-grey-400)",
    // Background colors
    ["--color-background-tertiary"]: "var(--color-grey-700)",
    ["--color-background-secondary-dark"]: "var(--color-background-secondary)",
    ["--color-background-overlay-dark"]: "var(--color-grey-700)",
    ["--color-background-overlay-light"]: "var(--color-background-overlay)",
    ["--color-background-badge"]: "var(--color-grey-300)",
    ["--color-background-light-blue"]: "var(--color-background-info-light)",
    ["--color-background-selector-active"]: "var(--color-grey-700)",
    ["--color-background-avatar-primary"]: "var(--color-grey-800)",
    ["--color-background-avatar-secondary"]: "var(--color-grey-600)",
    ["--color-background-dark"]: "var(--color-grey-400)",
    ["--color-background-dark-button"]: "var(--color-grey-50)",
    ["--color-background-plus-banner"]: "var(--color-grey-50)",
    ["--color-background-feature"]: "var(--color-haiti-400)",
    ["--color-online-background"]: "var(--color-background-success)",
    ["--color-background-banner"]: "var(--color-grey-0)",
    ["--color-background-toggle-active"]: "var(--color-grey-400)",
    ["--color-background-toggle-inactive"]: "var(--color-grey-700)",
    ["--color-background-modal-overlay"]: "var(--color-background-hover)",
    ["--color-background-text-editor-modifier"]: "#1a1d21", // NO COMPASS TOKEN — close to grey-900 #191b1f but not exact
    ["--color-background-text-editor-modifier-hover"]: "#2C2F33", // NO COMPASS TOKEN — close to grey-800 #2b2e33 but not exact
    ["--color-background-text-editor-modifier-active"]: "#444649", // NO COMPASS TOKEN — close to grey-700 #42464d but not exact
    ["--color-toggle-checked"]: "var(--color-grey-0)",
    ["--color-toggle-unchecked"]: "var(--color-grey-500)",
    ["--color-toggle-circle-checked"]: "#34373D", // NO COMPASS TOKEN — not in palette
    ["--color-toggle-circle-unchecked"]: "var(--color-grey-0)",
    ["--color-toggle-fullscreen"]: "var(--color-grey-400)",
    ["--color-circle-button-background"]: "var(--color-grey-0)",
    ["--color-circle-button-background-hover"]: "#e6e6e6", // NO COMPASS TOKEN — not in grey palette
    ["--color-ai-start"]: "var(--color-royal-blue-400)",
    ["--color-ai-end"]: "#AF81EA", // NO COMPASS TOKEN — not in palette (indigo-300 is #aa91dd, different)
    ["--color-chip-background"]: "var(--color-background-success-light)",
    ["--color-search-yellow-background"]: "var(--color-background-highlight)",
    ["--color-background-workflows-icon-wrapper"]:
      "var(--color-background-info-light)",
    ["--color-background-workflows-badge"]:
      "var(--color-background-info-light)",
    ["--color-background-xy-flow"]: "var(--color-grey-900)",
    ["--color-background-workflows-v2-error"]: "var(--color-background-danger)",
    // Border colors
    ["--color-border-secondary-dark"]: "#34373D", // NO COMPASS TOKEN — not in palette
    ["--color-border-tertiary"]: "var(--color-grey-800)",
    ["--color-border-dark"]: "var(--color-grey-600)",
    ["--color-border-very-dark"]: "var(--color-grey-50)",
    ["--color-border-darkest"]: "var(--color-grey-50)",
    ["--color-border-light"]: "var(--color-grey-400)",
    ["--color-border-input"]: "var(--color-border-accent)",
    ["--color-border-selector-inactive"]: "var(--color-grey-600)",
    ["--color-border-circle-button"]: "var(--color-grey-0)",
    ["--color-border-circle-button-hover"]: "#e6e6e6", // NO COMPASS TOKEN — not in grey palette
    ["--color-border-workflows-icon-wrapper"]: "var(--color-border-info)",
    ["--color-border-workflows-v2-error"]: "var(--color-border-danger)",
    // Community sidebar & switcher colors
    ["--color-community-sidebar-text"]: "var(--color-grey-200)",
    ["--color-community-sidebar-icon"]: "var(--color-grey-200)",
    ["--color-community-sidebar-icon-hover"]: "var(--color-grey-200)",
    ["--color-community-sidebar-badge-text"]: "var(--color-grey-0)",
    ["--color-community-sidebar-background"]:
      "var(--color-background-secondary)",
    ["--color-community-sidebar-active-background"]:
      "var(--color-royal-blue-400)",
    ["--color-community-sidebar-hover-background"]: "var(--color-grey-700)",
    ["--color-community-sidebar-badge-background"]:
      "var(--color-background-danger)",
    ["--color-message-highlight-background"]:
      "var(--color-background-warning-light)",
    ["--color-community-sidebar-border"]: "var(--color-border-secondary)",
    ["--color-community-switcher-new-community-button-icon"]:
      "var(--color-grey-0)",
    ["--color-community-switcher-background"]: "var(--color-grey-800)",
    ["--color-community-switcher-new-community-button-bg"]:
      "var(--color-grey-0)",
    // Event
    ["--color-event-sidebar-item"]: "var(--color-grey-600)",
    //Ring colors
    ["--color-ring-primary"]: "var(--color-grey-800)",
    ["--color-ring-secondary"]: "var(--color-grey-300)",
    // Support widget colors
    ["--color-support-widget-background"]: "var(--color-grey-700)",
    // Emoji picker colors — raw RGB channel format, cannot use CSS vars directly
    ["--color-background-emoji-picker"]: "43, 46, 51", // NO COMPASS TOKEN (FORMAT) — grey-800 in rgb channels for use with rgb()
    ["--color-background-emoji-picker-hover"]: "66, 70, 77", // NO COMPASS TOKEN (FORMAT) — grey-700 in rgb channels for use with rgb()
    ["--color-text-emoji"]: "210, 215, 219", // NO COMPASS TOKEN (FORMAT) — grey-300 in rgb channels for use with rgb()
    // Circle v2.1 colors
    ["--color-sidebar-footer-live-indicator"]: "var(--color-grey-700)",
    ["--color-community-header-background"]:
      "var(--color-background-secondary)",
    ["--color-community-header-hover-background"]: "var(--color-grey-700)",
    ["--color-community-header-text"]: "var(--color-grey-200)",
    // Circle v3.0 colors
    ["--color-community-header-active-text"]: "var(--color-grey-200)",
    ["--color-community-header-active-background"]: "var(--color-grey-700)",
    // Feedback colors
    ["--color-feedback-error"]: "var(--color-red-500)",
  },
};
